
















































































import { Component, Watch, Mixins, Ref } from "vue-property-decorator";
import { Action, State } from "vuex-class";

import AppHeader from "@/components/AppHeader/AppHeader.vue";
import AppContent from "@/components/AppContent/AppContent.vue";
import BasePopup from "@/components/BasePopup/BasePopup.vue";
import BaseButton from "@/components/BaseButton/BaseButton.vue";

import Step1 from "./Step1.vue";
import Step2 from "./Step2.vue";
import Step21 from "./Step2-1.vue";
import Step3 from "./Step3.vue";

import RequestState from "@/utils/mixins/RequestState";
import { IVuexState } from "@/models/interfaces/store";
import { ILesson, IActivity } from "@/models/interfaces/lessons";
import { ISchool } from "@/models/interfaces/schools";

import { AxiosPromise } from "axios";
import { IClass } from "@/models/interfaces/classes";
import amplitude from "amplitude-js";
import { eventCodeByName, EventsNames } from "@/analytics";

export enum Step {
  Assignee = "assignee",
  Lessons = "lessons",
  Activities = "activities",
  Confirmation = "confirmation"
}

type Popup = {
  title: string;
  buttonTitle: string;
  action?: string;
};

export interface LessonsFormData {
  classesIds: string[];
  subGroupsIds: string[];
  schoolsIds: string[];
  lessonId: string;
}

@Component({
  components: {
    AppHeader,
    AppContent,

    Step1,
    Step2,
    Step21,
    Step3,

    BasePopup,
    BaseButton
  }
})
export default class LessonsView extends Mixins(RequestState) {
  @Ref("step1")
  private firstStep!: HTMLDivElement & {
    setAutoSetupLoadingState: (status: boolean) => void;
  };

  @State("schools", { namespace: "schools" })
  private schools!: IVuexState<ISchool[]>;

  @State("classes", { namespace: "classes" })
  private classes!: IVuexState<IClass[]>;

  @State("lessonData", { namespace: "lessons" })
  private lessonData!: IVuexState<{
    activities: IActivity[];
    lesson: ILesson;
  }>;

  @State("lessons", { namespace: "lessons" })
  private lessons!: IVuexState<ILesson[]>;

  @Action("lessons/fetchLessons")
  private fetchLessons!: (levelId?: string) => Promise<ILesson[]>;

  @Action("lessons/fetchActivitiesForLesson")
  private fetchActivitiesForLesson!: (lessonId: number) => Promise<IActivity[]>;

  @Action("lessons/setLessonToClasses")
  private setLessonToClasses!: (data: {
    lessonId: string;
    classesIds: string[];
  }) => Promise<{ count: number }>;

  @Action("lessons/setLessonToSubGroups")
  private setLessonToSubGroup!: (data: {
    lessonId: string;
    subGroupsIds: string[];
  }) => Promise<{ count: number }>;

  @Action("schools/fetchSchools")
  private fetchSchools!: () => Promise<{ schools: ISchool[] }>;

  @Action("classes/fetchClassesForSchool")
  private fetchClassesForSchool!: (schoolsIds: string[]) => Promise<IClass[]>;

  @Action("lessons/unassignLessonFromClasses")
  private unassignLessonFromClasses!: (classesIds: string[]) => AxiosPromise;

  @Action("lessons/unassignLessonFromSubgroups")
  private unassignLessonFromSubgroups!: (
    subgroupsIds: string[]
  ) => AxiosPromise;

  @Watch("selectedLesson")
  private onSelectedLessonChange(nextValue: number, prevValue: number) {
    this.fetchActivitiesForLesson(nextValue);
  }

  private step = Step.Assignee;
  private showPopup = false;
  private selectedLesson = 0;
  private loading = false;

  private popup: Popup = {
    title: "",
    buttonTitle: "",
    action: ""
  };

  // Form data:
  private form: LessonsFormData = {
    schoolsIds: [],
    classesIds: [],
    subGroupsIds: [],
    lessonId: ""
  };

  private handleActivityClick(lesson: number) {
    this.setStep(Step.Activities);
    this.selectedLesson = lesson;
  }

  private async onStep1Next(lessonAssignmentType: "auto" | "manual") {
    if (lessonAssignmentType === "auto") {
      const setLoading = this.firstStep.setAutoSetupLoadingState;

      setLoading(true);

      const unassignAction = this.form.subGroupsIds.length
        ? () => this.unassignLessonFromSubgroups(this.form.subGroupsIds)
        : () => this.unassignLessonFromClasses(this.form.classesIds);

      unassignAction()
        .then(() => {
          this.popup.title = this.$t(
            "views.lessons.messages.setLessonsAuto"
          ) as string;
          this.popup.buttonTitle = this.$t("common.returnHome") as string;
          this.popup.action = "home";

          this.showPopup = true;
        })
        .catch(() => {
          this.popup.title = this.$t("errors.unexpected") as string;
          this.popup.buttonTitle = "Close";
          this.showPopup = true;
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      amplitude
        .getInstance()
        .logEvent(
          `${eventCodeByName(EventsNames.UserClicksAssignMyself)} ${
            EventsNames.UserClicksAssignMyself
          }`
        );
      this.setStep(Step.Lessons);
    }
  }

  private handleInputChange(input: keyof LessonsFormData, value: any) {
    this.form[input] = value;
  }

  private setStep(step: Step) {
    this.step = step;
  }

  private onPopupSubmit() {
    this.showPopup = false;

    switch (this.popup.action) {
      case "home":
        this.$router.push("/home");
        break;
      default:
        amplitude
          .getInstance()
          .logEvent(
            `${eventCodeByName(EventsNames.UserClicksAssignAnotherLesson)} ${
              EventsNames.UserClicksAssignAnotherLesson
            }`
          );
        this.form = {
          schoolsIds: [],
          classesIds: [],
          subGroupsIds: [],
          lessonId: ""
        };

        this.step = Step.Assignee;
        break;
    }
  }

  private handleSetLesson() {
    this.loading = true;

    amplitude
      .getInstance()
      .logEvent(
        `${eventCodeByName(EventsNames.UserClicksAssignLesson)} ${
          EventsNames.UserClicksAssignLesson
        }`
      );

    const { lessonId, classesIds, subGroupsIds } = this.form;

    const assignAction = this.form.subGroupsIds.length
      ? () => this.setLessonToSubGroup({ lessonId, subGroupsIds })
      : () =>
          this.setLessonToClasses({
            lessonId,
            classesIds
          });

    assignAction()
      .then(() => {
        this.popup = {
          title: this.$t("views.lessons.hasBeenAssigned") as string,
          buttonTitle: this.$t("views.lessons.assignAnother") as string
        };

        this.fetchSchools();
        this.fetchLessons();

        this.showPopup = true;
      })
      .catch(error => {
        this.popup = {
          title: this.$t("errors.unexpected") as string,
          buttonTitle: this.$t("common.close") as string
        };

        this.showPopup = true;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public mounted() {
    this.fetchLessons();
    this.fetchSchools();
  }

  @Watch("form.schoolsIds")
  private onSchoolChange(values: string[]) {
    if (values) {
      this.fetchClassesForSchool(values);
    }
  }
}
